import { Link } from 'react-router-dom';
import type { StoryCard } from '../../api/discover';
import { Badge } from '../ui';
import './StoryCardView.css';

export interface StoryCardViewProps {
  card: StoryCard;
  /** Narrowing the stream by a tag, from the card that showed it. */
  onTag?: (slug: string) => void;
  onCategory?: (slug: string) => void;
}

function when(card: StoryCard): string {
  if (!card.first_published_at) return '';
  return new Date(card.first_published_at).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * One story in a stream.
 *
 * A card has one job: help someone decide whether to open this. So it leads
 * with the title and the author's own first sentence — never a chart, never a
 * number pulled out of context, because a figure without its story is the thing
 * this product exists not to publish.
 */
export function StoryCardView({ card, onTag, onCategory }: StoryCardViewProps) {
  return (
    <article className="sc-card-story">
      <div className="sc-card-story__meta">
        {card.featured_at && <Badge tone="accent">Featured</Badge>}
        {card.category && (
          <button
            type="button"
            className="sc-card-story__topic"
            onClick={() => onCategory?.(card.category?.slug ?? '')}
          >
            {card.category.name}
          </button>
        )}
        <span className="sc-card-story__by">
          {/* Both, unless somebody's display name simply is their handle. */}
          {card.author.display_name === card.author.handle
            ? `@${card.author.handle}`
            : `${card.author.display_name} · @${card.author.handle}`}
        </span>
      </div>

      <h2 className="sc-card-story__title">
        <Link to={`/stories/${card.id}`}>{card.title}</Link>
      </h2>
      {card.subtitle && <p className="sc-card-story__subtitle">{card.subtitle}</p>}
      {card.excerpt && <p className="sc-card-story__excerpt">{card.excerpt}</p>}

      <div className="sc-card-story__foot">
        <span>
          {when(card)}
          {card.view_count > 0 && ` · ${card.view_count} reader${card.view_count === 1 ? '' : 's'}`}
          {/* Public, and only when there is something to say: a row of zeros
              tells a reader nothing except that nobody has been here. */}
          {card.like_count > 0 && ` · ${card.like_count} like${card.like_count === 1 ? '' : 's'}`}
          {card.comment_count > 0 &&
            ` · ${card.comment_count} comment${card.comment_count === 1 ? '' : 's'}`}
        </span>
        <span className="sc-card-story__tags">
          {card.tags.map((tag) => (
            <button
              key={tag.slug}
              type="button"
              className="sc-card-story__topic"
              onClick={() => onTag?.(tag.slug)}
            >
              #{tag.name}
            </button>
          ))}
        </span>
      </div>
    </article>
  );
}
